import { ArrowDropDown, Delete, Edit, Search } from "@mui/icons-material";
import { Button, Menu, MenuItem } from "@mui/material";
import { styled } from "@mui/material/styles";
import { get, isNil } from "lodash";
import React, { useEffect, useState } from "react";
import Autocomplete from "../../../common/js/components/Autocomplete";
import BSInput from "../../../common/js/components/BSInput";
import BSSelect from "../../../common/js/components/BSSelect";
import { Add, Subdirectory } from "../../../common/js/icons";
import searchConstants from "../search/constants";
import { getNonNull } from "../utils/operations";
import "./BrokerCommissions.css";
import OutsideSplitModal from "./OutsideSplitModal";
import { department_options, representation_type_options } from "./constants";

const StyledShrinkButton = styled(Button)({
    padding: 0,
    minWidth: 0,
    minHeight: 0,
    margin: 0,
    color: "#9e9e9e",
    "&:hover": {
        backgroundColor: "transparent",
    },
});

const StyledAddRunner = styled(Button)({
    margin: 0,
    color: "#2C99D2",
    "&:hover": {
        backgroundColor: "transparent",
    },
});

const StyledCenterCell = styled("div")({
    textAlign: "center",
    fill: "#9e9e9e",
    marginTop: "30px",
    paddingLeft: "1vw",
});

const StyledCommissionComment = styled("div")({
    fontSize: "13px",
    lineHeight: "26px",
    borderRadius: "3px",
    border: "1px solid #ccc",
    padding: "5px",
    marginTop: "-22px",
    width: "calc((100% - 35px) - 2vw)",
});

const StyledBrokerCommissionContainer = styled("div")({
    maxHeight: "105px",
    marginBottom: "10px",
    display: "flex",
});

const BrokerCommissions = (props) => {
    const {
        autocompleteResults,
        brokerCommissions,
        errors,
        formDisabled,
        handleAddBrokerCommission,
        handleAddRunner,
        handleDeleteOutsideSplit,
        handleReducerLevelInput,
        handleRemoveCommissionOrRunner,
        handleSearchCompany,
        handleSearchKmBrokers,
        handleSearchOutsideBroker,
        handleSaveOutsideSplit,
        handleSearchPerson,
        outsideSplitModal,
        searchResults,
        toggleOutsideSplitModal,
        updateSplit,
        voucher,
        userProfile,
        elementsSaving,
    } = props;

    const isUserRestrictedFromAddingCommissions =
        !["Draft", "Broker Action Required", "Awaiting Payment"].includes(voucher.voucherStatus) &&
        userProfile.role === "broker";
    const [initialBrokerCommissions, setInitialBrokerCommissions] = useState([]);
    const [kmCommission, setKmCommission] = useState(0);
    const [outsideCommission, setOutsideCommission] = useState(0);
    const [anchorEl, setAnchorEl] = useState(null);

    useEffect(() => {
        if (elementsSaving.saveAndContinue === false) {
            setInitialBrokerCommissions(Object.keys(filterNonValidBrokers()));
        }
    }, [elementsSaving.saveAndContinue]);

    useEffect(() => {
        getBrokerCommissions();
        if (initialBrokerCommissions.length === 0 && Object.keys(brokerCommissions).length > 0) {
            setInitialBrokerCommissions(Object.keys(filterNonValidBrokers()));
        }
    }, [brokerCommissions]);

    const filterNonValidBrokers = () => {
        return Object.fromEntries(
            Object.entries(brokerCommissions).filter(([key, commission]) => {
                return (
                    !isNil(commission.representationType) &&
                    !isNil(commission.brokerAgentKey) &&
                    commission?.firstName &&
                    commission?.lastName
                );
            })
        );
    };

    const isCommissionDisabledForBroker = (commissionKey) => {
        return (
            userProfile.role === "broker" &&
            (!["Draft", "Broker Action Required", "Awaiting Payment"].includes(voucher.voucherStatus) ||
                (voucher.voucherStatus === "Broker Action Required" &&
                    (initialBrokerCommissions === null || initialBrokerCommissions?.includes(commissionKey))))
        );
    };

    const handleChange = (splitId, event) => {
        props.updateSplit(splitId, event.target.id, event.target.value, "voucher");
    };

    const handleRunnerChange = (splitId, runnerId, event) => {
        props.updateRunner(splitId, runnerId, event.target.id, event.target.value);
    };

    const handleCommissionSum = (event) => {
        props.handleVoucherChange(event);
    };

    const handleBrokerPersonChange = (person, splitKey, source) => {
        props.updateSplit(splitKey, "brokerAgentKey", person["brokerAgentKey"], source);
        props.updateSplit(splitKey, "firstName", person["firstName"], source);
        props.updateSplit(splitKey, "lastName", person["lastName"], source);
        props.updateSplit(splitKey, "personKey", person["personKey"], source);
    };

    const handleRunnerPersonChange = (person, splitKey, runnerKey) => {
        props.updateRunner(splitKey, runnerKey, "brokerAgentKey", person["brokerAgentKey"]);
        props.updateRunner(splitKey, runnerKey, "firstName", person["firstName"]);
        props.updateRunner(splitKey, runnerKey, "lastName", person["lastName"]);
        props.updateRunner(splitKey, runnerKey, "personKey", person["personKey"]);
    };

    const createOutsideSplitDraft = (isFreeformSearchEnabled, commissionType) => {
        props.handleCreateOutsideSplitDraft(isFreeformSearchEnabled, commissionType);
    };

    const freeformUpdate = (splitKey, value, source = "voucher") => {
        const splitName = value.split(" ");
        if (splitName.length > 1) {
            const firstName = splitName
                .slice(0, splitName.length - 1)
                .join(" ")
                .trim();
            const lastName = splitName
                .slice(splitName.length - 1)
                .join(" ")
                .trim();
            props.updateSplit(splitKey, "brokerAgentKey", null, source);
            props.updateSplit(splitKey, "firstName", firstName, source);
            props.updateSplit(splitKey, "lastName", lastName, source);
            props.updateSplit(splitKey, "personKey", null, source);
        } else {
            props.updateSplit(splitKey, "brokerAgentKey", null, source);
            props.updateSplit(splitKey, "firstName", value, source);
            props.updateSplit(splitKey, "lastName", "", source);
            props.updateSplit(splitKey, "personKey", null, source);
        }
    };

    const isKMBroker = (commission) => {
        const isKMBroker = get(commission, "kmBrokerFlag");
        const isKMCompany = getNonNull(commission, "company.legalName", "").includes("Kidder Mathews");
        return isKMBroker || isKMCompany;
    };

    const getBrokerCommissions = () => {
        let updatedKmCommission = 0;
        let updatedOutsideCommission = 0;

        Object.keys(brokerCommissions).forEach((c) => {
            const commission = brokerCommissions[c];
            let netAmount = commission.netAmount ? commission.netAmount : 0;
            // note: netAmount will be a formatted string when it is extracted from commission. Converting to float for use in below calculations.
            netAmount = parseFloat(netAmount.toString().replaceAll(",", ""));
            if (isKMBroker(commission)) {
                updatedKmCommission += netAmount;
            } else {
                updatedOutsideCommission += netAmount;
            }
        });
        setKmCommission(updatedKmCommission.toFixed(2));
        setOutsideCommission(updatedOutsideCommission.toFixed(2));
    };

    const getDepartment = (suggestion) => {
        const departmentNames = searchConstants["departments"];
        if (
            get(suggestion, "department", null) !== null &&
            get(suggestion, "department", null) !== "" &&
            departmentNames[get(suggestion, "department", "N/A")] !== undefined
        ) {
            return departmentNames[get(suggestion, "department", "N/A")]["display"];
        } else {
            return departmentNames["N/A"]["display"];
        }
    };

    const getKMBrokerAndRunnerDisplay = (suggestion) => {
        let brokerName = get(suggestion, "firstName", "") + " " + get(suggestion, "lastName", "");
        let departmentType = getDepartment(suggestion);
        let brokerOffice = get(suggestion, "officeName", "");
        if (brokerOffice === null) brokerOffice = "N/A";
        return brokerName + " - " + departmentType + " - " + brokerOffice;
    };

    return (
        <div>
            {outsideSplitModal.key && (
                <OutsideSplitModal
                    open={outsideSplitModal.isOpen}
                    toggleOutsideSplitModal={toggleOutsideSplitModal}
                    updateSplit={updateSplit}
                    searchResults={searchResults}
                    handleReducerLevelInput={handleReducerLevelInput}
                    handleSearchCompany={handleSearchCompany}
                    handleDeleteOutsideSplit={handleDeleteOutsideSplit}
                    saveOutsideSplit={handleSaveOutsideSplit}
                    handleSearchPerson={handleSearchPerson}
                    handleSearchOutsideBroker={handleSearchOutsideBroker}
                    handleBrokerPersonChange={handleBrokerPersonChange}
                    values={outsideSplitModal.brokerCommissions[outsideSplitModal.key]}
                    splitId={outsideSplitModal.key}
                    secondaryAction={outsideSplitModal.secondaryAction}
                    autocompleteResults={autocompleteResults}
                    errors={get(errors, "outsideSplitModal", {})}
                    freeformUpdate={freeformUpdate}
                    isFreeformSearchEnabled={outsideSplitModal.isFreeformSearchEnabled}
                    commissionType={outsideSplitModal.commissionType}
                />
            )}
            <div style={{ display: "flex", alignItems: "center" }}>
                <div>
                    <h3>Installment Commission Allocation</h3>
                </div>
                {!formDisabled && (
                    <div>
                        <React.Fragment>
                            <Button
                                variant="contained"
                                color="primary"
                                disabled={formDisabled || isUserRestrictedFromAddingCommissions}
                                onClick={(event) => setAnchorEl(event.currentTarget)}
                                endIcon={<ArrowDropDown />}
                            >
                                + Add
                            </Button>
                            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                                <MenuItem
                                    onClick={() => {
                                        handleAddBrokerCommission();
                                        setAnchorEl(null);
                                    }}
                                >
                                    Add KM Broker
                                </MenuItem>
                                <MenuItem
                                    onClick={() => {
                                        createOutsideSplitDraft(true, "OUTSIDE BROKER");
                                        setAnchorEl(null);
                                    }}
                                >
                                    Add Outside Broker
                                </MenuItem>
                                <MenuItem
                                    onClick={() => {
                                        createOutsideSplitDraft(false, "REFERRAL");
                                        setAnchorEl(null);
                                    }}
                                >
                                    Add Outside Referral
                                </MenuItem>
                            </Menu>
                        </React.Fragment>
                    </div>
                )}
            </div>
            {Object.keys(brokerCommissions).map((c, i) => {
                const commission = brokerCommissions[c];
                let officeName = get(commission, `brokerAgent.office.officeName`, "");
                if (
                    get(commission, "company.legalName", null) !== null
                        ? get(commission, "company.legalName", null).includes("Kidder Mathews")
                        : commission.kmBrokerFlag
                ) {
                    return (
                        <React.Fragment>
                            <StyledBrokerCommissionContainer key={`split-${i}`}>
                                <div className="displayFlex brokerRepresentationContainer">
                                    <div className="commissionBrokerName">
                                        <Autocomplete
                                            id="firstName"
                                            label={"Km Broker"}
                                            error={get(errors, `${c}.firstName`, "")}
                                            initialInputValue={`${get(commission, `firstName`, "")}${
                                                get(commission, `brokerAgentKey`, null) ? " " : ""
                                            }${get(commission, `lastName`, "")}`}
                                            itemToString={(i) => get(i, `firstName`, null)}
                                            onSelect={(p, b) => handleBrokerPersonChange(p, c, "voucher")}
                                            placeholder="Enter broker"
                                            errorText="Required"
                                            helperText={officeName ? `KM Office: ${officeName}` : ""}
                                            searchFunction={handleSearchKmBrokers}
                                            startAdornment={i !== 0 && <Search />}
                                            suggestions={autocompleteResults}
                                            disabled={
                                                (!isNil(get(commission, `brokerAgentKey`, null)) && i === 0) ||
                                                formDisabled ||
                                                isCommissionDisabledForBroker(c)
                                            }
                                            options={{
                                                key: "brokerAgentKey",
                                                display: (suggestion) => {
                                                    return getKMBrokerAndRunnerDisplay(suggestion);
                                                },
                                            }}
                                            extraFormControlStyles={{ marginBottom: 0 }}
                                        />
                                    </div>
                                    <div className="commissionRepresentationType">
                                        <BSSelect
                                            id="representationType"
                                            combinedField="shiftLeft"
                                            value={commission.representationType}
                                            error={get(errors, `${c}.representationType`, null)}
                                            onChange={(e) => handleChange(c, e)}
                                            errorText="Required"
                                            helperText="Required"
                                            placeholder="Representation"
                                            options={representation_type_options}
                                            disabled={formDisabled || isCommissionDisabledForBroker(c)}
                                            extraFormControlStyles={{ marginBottom: 0 }}
                                        />
                                    </div>
                                </div>
                                <div className="brokerAllocationContainer">
                                    <div className="flexGrowTriple commissionMarginRight">
                                        <BSSelect
                                            id="department"
                                            value={commission.department}
                                            error={get(errors, `${c}.department`, null)}
                                            onChange={(e) => handleChange(c, e)}
                                            errorText="Required"
                                            helperText="Required"
                                            placeholder="Department"
                                            options={department_options}
                                            disabled={formDisabled}
                                            extraFormControlStyles={{ marginBottom: 0 }}
                                        />
                                    </div>
                                    <div className="flexGrowSingle commissionMarginRight commissionNetPercent">
                                        <BSInput
                                            id="netPercent"
                                            value={commission.netPercent}
                                            error={get(errors, `${c}.netPercent`, null)}
                                            onChange={(e) => handleChange(c, e)}
                                            errorText="Required"
                                            startAdornment="%"
                                            disabled={formDisabled}
                                        />
                                    </div>
                                    <div
                                        className={
                                            i === 0 && !formDisabled
                                                ? "flexGrowDouble initialCommission"
                                                : "flexGrowDouble"
                                        }
                                    >
                                        <BSInput
                                            id="netAmount"
                                            value={commission.netAmount}
                                            error={get(errors, `${c}.netAmount`, null)}
                                            onChange={(e) => handleChange(c, e)}
                                            errorText="Required"
                                            startAdornment="$"
                                            disabled={formDisabled}
                                            numeric
                                        />
                                    </div>

                                    {i !== 0 && !formDisabled && !isCommissionDisabledForBroker(c) && (
                                        <StyledCenterCell>
                                            <StyledShrinkButton
                                                onClick={() => handleRemoveCommissionOrRunner({ key: c })}
                                                disabled={formDisabled || isCommissionDisabledForBroker(c)}
                                            >
                                                <Delete />
                                            </StyledShrinkButton>
                                        </StyledCenterCell>
                                    )}
                                </div>
                            </StyledBrokerCommissionContainer>
                            {Object.keys(brokerCommissions[c].runnerCommissions).map((r, i) => {
                                const runner = brokerCommissions[c]["runnerCommissions"][r];
                                return (
                                    <div
                                        key={`splits-runner-${r}`}
                                        style={{ display: "flex", gap: "1vw", marginRight: "1vw" }}
                                    >
                                        <StyledCenterCell>
                                            <Subdirectory />
                                        </StyledCenterCell>
                                        <div className="flexGrowTriple">
                                            <Autocomplete
                                                id="firstName"
                                                label={"Junior Associate"}
                                                error={get(errors, `${c}.runners.${r}.firstName`, null)}
                                                errorText="Required"
                                                initialInputValue={`${get(runner, "firstName", "")}${
                                                    get(runner, "firstName") && get(runner, "lastName") ? " " : ""
                                                }${get(runner, "lastName", "")}`}
                                                itemToString={(i) => get(i, "firstName", null)}
                                                onSelect={(p, b) => handleRunnerPersonChange(p, c, r)}
                                                placeholder="Enter junior associate"
                                                searchFunction={handleSearchPerson}
                                                startAdornment={i !== 0 && <Search />}
                                                suggestions={searchResults}
                                                disabled={formDisabled}
                                                options={{
                                                    key: "brokerAgentKey",
                                                    display: (suggestion) => {
                                                        return getKMBrokerAndRunnerDisplay(suggestion);
                                                    },
                                                }}
                                            />
                                        </div>
                                        <div className="flexGrowSingle">
                                            <BSInput
                                                id="percentOfBrokerNet"
                                                value={runner.percentOfBrokerNet}
                                                error={get(errors, `${c}.runners.${r}.percentOfBrokerNet`, null)}
                                                onChange={(e) => handleRunnerChange(c, r, e)}
                                                errorText="Required"
                                                startAdornment="%"
                                                disabled={
                                                    formDisabled
                                                        ? true
                                                        : runner.amount !== 0 && runner.amount !== null
                                                          ? true
                                                          : false
                                                }
                                            />
                                        </div>
                                        <div className="flexGrowSingle">
                                            <BSInput
                                                id="amount"
                                                value={
                                                    runner.percentOfBrokerNet === 0 ||
                                                    runner.percentOfBrokerNet === null
                                                        ? runner.amount
                                                        : null
                                                }
                                                error={get(errors, `${c}.runners.${r}.amount`, null)}
                                                onChange={(e) => handleRunnerChange(c, r, e)}
                                                errorText="Required"
                                                startAdornment="$"
                                                disabled={
                                                    formDisabled
                                                        ? true
                                                        : runner.percentOfBrokerNet !== 0 &&
                                                            runner.percentOfBrokerNet !== null
                                                          ? true
                                                          : false
                                                }
                                                numeric
                                            />
                                        </div>
                                        {!formDisabled && (
                                            <StyledCenterCell>
                                                <StyledShrinkButton
                                                    onClick={() =>
                                                        handleRemoveCommissionOrRunner({ key: r, parent: c })
                                                    }
                                                >
                                                    <Delete />
                                                </StyledShrinkButton>
                                            </StyledCenterCell>
                                        )}
                                    </div>
                                );
                            })}
                            {!formDisabled && (
                                <div>
                                    <StyledAddRunner color="primary" onClick={() => handleAddRunner(c)}>
                                        <Add fill="#4b94c1" />
                                        Add Junior Associate
                                    </StyledAddRunner>
                                </div>
                            )}
                        </React.Fragment>
                    );
                } else {
                    return null;
                }
            })}
            {Object.keys(brokerCommissions).map((s, i) => {
                const split = brokerCommissions[s];
                const commissionType = get(split, "commissionType") === "REFERRAL" ? "REFERRAL" : "OUTSIDE BROKER";
                const isFreeformSearchEnabled = commissionType === "REFERRAL" ? false : true;
                const outsideCommissionHeader = commissionType === "REFERRAL" ? "OUTSIDE REFERRAL" : "OUTSIDE BROKER";
                const commissionComment = get(split, "commissionComment.comment");
                if (
                    get(split, "company.legalName", null) !== null
                        ? !get(split, "company.legalName", null).includes("Kidder Mathews")
                        : !split.kmBrokerFlag
                ) {
                    return (
                        <React.Fragment>
                            <StyledBrokerCommissionContainer key={`split-${i}`}>
                                <div className="displayFlex brokerRepresentationContainer">
                                    <div className="commissionBrokerName">
                                        <BSInput
                                            id="firstName"
                                            label={outsideCommissionHeader}
                                            value={`${get(split, `firstName`, "")}${
                                                get(split, `firstName`, null) ? " " : ""
                                            }${get(split, `lastName`, "")}`}
                                            errorText="Required"
                                            disabled={true}
                                        />
                                    </div>
                                    <div className="commissionRepresentationType">
                                        <BSSelect
                                            id="representationType"
                                            combinedField="shiftLeft"
                                            value={split.representationType}
                                            error={get(errors, `${s}.representationType`, null)}
                                            errorText="Required"
                                            onChange={(e) => handleChange(s, e)}
                                            placeholder="Representation"
                                            options={representation_type_options}
                                            disabled={formDisabled}
                                        />
                                    </div>
                                </div>
                                <div className="brokerAllocationContainer">
                                    <div className="flexGrowTriple commissionMarginRight">
                                        <BSSelect
                                            id="department"
                                            value={split.department}
                                            error={get(errors, `${s}.department`, null)}
                                            onChange={(e) => handleChange(s, e)}
                                            errorText="Required"
                                            placeholder="Department"
                                            options={[
                                                {
                                                    value: "",
                                                    label: "Department",
                                                    disabled: true,
                                                },
                                            ]}
                                            disabled={true}
                                        />
                                    </div>
                                    <div className="flexGrowSingle commissionMarginRight commissionNetPercent">
                                        <BSInput
                                            id="netPercent"
                                            value={split.netPercent}
                                            error={get(errors, `${s}.netPercent`, null)}
                                            onChange={(e) => handleChange(s, e)}
                                            errorText="Required"
                                            startAdornment="%"
                                            disabled={formDisabled}
                                        />
                                    </div>
                                    <div className="flexGrowDouble">
                                        <BSInput
                                            id="netAmount"
                                            value={split.netAmount}
                                            error={get(errors, `${s}.netAmount`, null)}
                                            onChange={(e) => handleChange(s, e)}
                                            errorText="Required"
                                            startAdornment="$"
                                            disabled={formDisabled}
                                            numeric={true}
                                        />
                                    </div>
                                    {!formDisabled && (
                                        <StyledCenterCell>
                                            <StyledShrinkButton
                                                onClick={() =>
                                                    toggleOutsideSplitModal({
                                                        key: s,
                                                        split: split,
                                                        secondaryAction: "Delete",
                                                        isFreeformSearchEnabled: isFreeformSearchEnabled,
                                                        commissionType: commissionType,
                                                    })
                                                }
                                                disabled={formDisabled}
                                            >
                                                <Edit />
                                            </StyledShrinkButton>
                                        </StyledCenterCell>
                                    )}
                                </div>
                            </StyledBrokerCommissionContainer>
                            {commissionComment && (
                                <StyledCommissionComment> {commissionComment} </StyledCommissionComment>
                            )}
                        </React.Fragment>
                    );
                } else {
                    return null;
                }
            })}
            <div>
                <div>
                    <h3>Installment Commission Totals</h3>
                </div>
                <div style={{ display: "flex", gap: "1vw" }}>
                    <div className="flexGrowSingle">
                        <BSInput
                            id="kmInstallmentComm"
                            label="KM Installment Commission"
                            value={kmCommission}
                            onChange={(e) => handleCommissionSum(e)}
                            startAdornment="$"
                            disabled={true}
                        />
                    </div>

                    <div className="flexGrowSingle">
                        <BSInput
                            id="outsideInstallmentComm"
                            label="Outside Installment Commission"
                            value={outsideCommission}
                            onChange={(e) => handleCommissionSum(e)}
                            startAdornment="$"
                            disabled={true}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BrokerCommissions;
